import React from "react";
import { styleResolvers } from "@fanduel/formation-shared-utils";
import {
  Box,
  ButtonPrimitive,
  useTheme
} from "@fanduel/formation-react-components";
import { IconButtonProps } from "./types";
import { iconButtonSizes, getThemeColor } from "./utils/styles";

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      qaLabel = "icon-button",
      as = "button",
      css,
      disabled = false,
      href,
      icon,
      onClick,
      overrides,
      size = "regular",
      variant = "tertiary",
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    return (
      <ButtonPrimitive
        data-qa-label={qaLabel}
        ref={ref}
        as={as}
        disabled={disabled}
        onPress={onClick ? () => onClick({} as React.MouseEvent) : undefined}
        href={href}
        role={as === "a" && href ? "link" : "button"}
      >
        {({ isDisabled, isFocused, isHovered, isPressed }, buttonProps) => {
          const styles = styleResolvers.button(
            {
              variant,
              size,
              description: false,
              loading: false,
              disabled,
              active: isPressed,
              hover: isHovered,
              focus: isFocused
            },
            overrides
          );

          return (
            <Box
              {...buttonProps}
              css={{
                justifyItems: "center",
                appearance: "none",
                cursor: isDisabled ? "not-allowed" : "pointer",
                textDecoration: "none",
                backgroundColor: getThemeColor(theme, styles.colour),
                color: getThemeColor(theme, styles.contentColour),
                borderColor: getThemeColor(theme, styles.borderColor),
                borderRadius: theme.radii.borderRadiusCircle,
                ...iconButtonSizes[size],
                ...css
              }}
              {...rest}
            >
              {icon}
            </Box>
          );
        }}
      </ButtonPrimitive>
    );
  }
);

IconButton.displayName = "IconButton";

export type { IconButtonProps };
export { IconButton };
